// Purchase return / purchase credit note presets.
// "purchaseReturnA4" recreates backend helper/return_invoice_types/return_inv_1.ts (ReturnInvoice_1)
// and "purchaseCreditNoteA4" recreates helper/debit-notes_types/DebitNote_1.ts (DebitNote_1),
// so the current hard-coded prints can be edited in the builder.
// Both prints are one bordered 15-column grid in Courier New; the column
// boundaries below are shared by the header blocks, the item table and the totals.
import { text, box, col, table, fixedBand, tableBand, a4, preset } from "./helpers";

const W = 194; // A4 content width with 8 mm margins
const FONT = { fontFamily: "Courier New", fontSize: 8, color: "#000000" };

// Item grid column widths (mm), in print order. Sum = W.
const COLS = [5, 17, 15, 27, 11, 13, 18, 8, 11, 11, 9, 13, 9, 14, 13];
const edge = (n) => COLS.slice(0, n).reduce((a, b) => a + b, 0); // right edge of the first n columns

const X_INFO = edge(11); // "From" cell spans 11 columns, document info the other 4
const X_PARTY = edge(7); // left party cell spans 7 columns, right one 8
const X_TOTALS = edge(12); // GST summary cell spans 12 columns
const X_VALUE = edge(14); // totals: label spans columns 13-14, value is column 15

// JS joinComma(...): non-blank parts joined with ", ".
const joinComma = (...exprs) => `MID(${exprs.map((e) => `IF(${e}, ", " & ${e}, "")`).join(" & ")}, 3)`;

// Party block exactly like the TS bill/ship blocks: heading, then (only when the
// address object exists) name, address line and the optional FSSAI / DL / GST / PAN lines.
function partyBlock(heading, addr, kind) {
  const f = (k) => `${addr}.cmr_${kind}_${k}`;
  const dl = joinComma(f("dl_no_20_20b"), f("dl_no_21_21b"), f("dl_no_21c"), f("dl_no_21f"));
  const body = [
    `IF(${f("address_name")}, "**" & ${f("address_name")} & "**", "")`,
    `"\\n" & ${joinComma(f("address_street"), f("address_town"), f("address_city"), f("address_state"), f("address_pincode"))}`,
    `IF(${f("fssai_no")}, "\\n**FSSAI NO:** " & ${f("fssai_no")}, "")`,
    `IF(${dl}, "\\n**DL NO:** " & ${dl}, "")`,
    `IF(${f("gst")}, "\\n**GST NO:** " & ${f("gst")}, "")`,
    `IF(${f("pan")}, "\\n**PAN NO:** " & ${f("pan")}, "")`,
  ].join(" & ");
  return `**${heading}**\n{{ IF(${addr}, ${body}, "") }}`;
}

const STORE_DL = `COALESCE(${joinComma("store.dl_no_20_20b", "store.dl_no_21_21b", "store.dl_no_21c", "store.dl_no_21f")}, store.company_licence, "-")`;

// cfg: { docType, name, title, noLabel, noExpr, dateLabel, dateExpr, left: [heading, addr, kind], right: [...],
//        totals: [[label, expr], ...], netExpr, remarksExpr }
function purchaseDocA4(cfg) {
  const T = { borderWidth: 0.75, borderColor: "#000000" };
  const cell = (x, y, w, h) => box(x, y, w, h, T);
  const totalsH = 30; // 6 rows x 5 mm (GST summary sits in the 12-column cell on the left)
  const rowH = totalsH / cfg.totals.length;

  return preset(
    cfg.docType,
    cfg.name,
    a4(8),
    [
      fixedBand(
        "Header",
        70,
        [
          cell(0, 0, W, 6),
          text(0, 0, W, 6, cfg.title, { bold: true, fontSize: 9, align: "center", valign: "middle" }),

          // From (store) | document number / date / payment terms
          cell(0, 6, X_INFO, 30),
          text(
            1,
            7,
            X_INFO - 2,
            28,
            "From,\n" +
              "**Store Code:**{{ COALESCE(store.store_code, store.store_id) | dash }}\n" +
              "**{{ store.company | dash }}**\n" +
              "{{ store.company_address | dash }}\n" +
              "**GST NO.:** {{ store.company_gstn | dash }}\n" +
              "**Pan NO.:** {{ COALESCE(store.pan_number, store.company_pan) | dash }}\n" +
              "**FSSAI NO.:** {{ store.fssai_no | dash }}\n" +
              `**D.L. No.:**  {{ ${STORE_DL} }}`
          ),
          cell(X_INFO, 6, W - X_INFO, 30),
          text(
            X_INFO + 1,
            7,
            W - X_INFO - 2,
            28,
            `**${cfg.noLabel}:** {{ ${cfg.noExpr} | dash }}\n**${cfg.dateLabel}:** {{ DATE(${cfg.dateExpr}, "DD/MM/YYYY") | dash }}\n**Payment Terms:**`,
            { fontSize: 7.5 }
          ),

          // Party blocks (12px text, line-height 1.4 in the original)
          cell(0, 36, X_PARTY, 34),
          text(1.5, 37.5, X_PARTY - 3, 31, partyBlock(...cfg.left), { fontSize: 8, lineHeight: 1.35 }),
          cell(X_PARTY, 36, W - X_PARTY, 34),
          text(X_PARTY + 1.5, 37.5, W - X_PARTY - 3, 31, partyBlock(...cfg.right), { fontSize: 8, lineHeight: 1.35 }),
        ],
        { repeat: "top" }
      ),
      tableBand(
        "Items",
        table(
          "items",
          "batches",
          0,
          W,
          [
            col("sno", "#", "SNO()", COLS[0]),
            col("code", "ProductCode", "item_code", COLS[1], { format: "dash" }),
            col("hsn", "HsnCode", "item_hsn", COLS[2], { format: "dash" }),
            col("name", "ProductName", "item_name", COLS[3], { format: "dash" }),
            col("uom", "UomName", "item_uom", COLS[4], { format: "dash" }),
            col("batch", "BatchNo", "item.item_batch_number", COLS[5]),
            col("exp", "Exp", "item.item_exp_date", COLS[6], { format: "date:YYYY-MM-DD | dash" }),
            col("qty", "Qty", "COALESCE(item.item_sellable_quantity, item.item_quantity, 0)", COLS[7]),
            col("rate", "Rate", "COALESCE(item.item_batch_purchase_rate, 0)", COLS[8], { format: "fixed:2" }),
            col("mrp", "MRP", "COALESCE(item.item_batch_unit_price, item.item_mrp, item.item_unit_price, 0)", COLS[9], { format: "fixed:2" }),
            col("disc", "Disc %", "COALESCE(item.item_batch_discount_percentage, item.item_discount_percentage, 0)", COLS[10], { format: "fixed:2" }),
            col(
              "subtotal",
              "Subtotal",
              "IF(item.item_batch_total_purchase_rate, item.item_batch_total_purchase_rate - col.taxamt, IF(item.item_total_amount && item.item_tax_amount, item.item_total_amount - item.item_tax_amount, 0))",
              COLS[11],
              { format: "fixed:2" }
            ),
            col("taxpct", "Tax %", "COALESCE(item.item_batch_tax_percentage, item.item_total_tax_percentage, item.item_gst, 0)", COLS[12], { format: "fixed:2" }),
            col("taxamt", "TaxAmount", "COALESCE(item.item_batch_tax_amount, item.item_tax_amount, 0)", COLS[13], { format: "fixed:2" }),
            col("amount", "Total Amount", "COALESCE(item.item_batch_total_purchase_rate, item.item_total_amount, 0)", COLS[14], { format: "fixed:2" }),
          ],
          {
            style: { fontSize: 7, grid: "all", borderWidth: 0.75, borderColor: "#000000", cellPadding: 0.6 },
            headerStyle: { bold: true, fontSize: 6.5 },
          }
        )
      ),
      fixedBand("Totals", 86, [
        // GST summary (half the width of the 12-column cell, like the original)
        cell(0, 0, X_TOTALS, totalsH),
        table(
          "gst_summary",
          "tax",
          0.4,
          X_TOTALS / 2,
          [
            col("gst", "GST%", "item_gst", 1, { format: "dash" }),
            col("taxable", "Taxable", "taxable", 1, { format: "dash" }),
            col("sgst", "SGST", "sgst", 1, { format: "dash" }),
            col("cgst", "CGST", "gst", 1, { format: "dash" }),
            col("igst", "IGST", "igst", 1, { format: "dash" }),
          ],
          {
            y: 0.4,
            h: totalsH - 0.8,
            repeatHeader: false,
            style: { fontSize: 7, grid: "all", borderWidth: 0.75, borderColor: "#000000", cellPadding: 0.5 },
            headerStyle: { bold: true },
          }
        ),
        ...cfg.totals.flatMap(([label, expr], i) => [
          cell(X_TOTALS, i * rowH, X_VALUE - X_TOTALS, rowH),
          text(X_TOTALS + 1, i * rowH, X_VALUE - X_TOTALS - 2, rowH, label, { fontSize: 7.5, valign: "middle", wrap: false }),
          cell(X_VALUE, i * rowH, W - X_VALUE, rowH),
          text(X_VALUE + 1, i * rowH, W - X_VALUE - 2, rowH, `{{ ${expr} | fixed:2 }}`, { fontSize: 7.5, valign: "middle", wrap: false }),
        ]),
        cell(0, 30, W, 6),
        text(1, 30, W - 2, 6, `Total Amount(in words): {{ ${cfg.netExpr} | words:after }}`, { valign: "middle" }),
        cell(0, 36, W, 6),
        text(1, 36, W - 2, 6, `Remarks: {{ ${cfg.remarksExpr} }}`, { valign: "middle" }),
        cell(0, 42, W, 26.5),
        cell(0, 68.5, W, 9),
        text(1, 68.5, W - 2, 9, "from, {{ store.company | dash }}\nAuthorised Signature", { align: "right", valign: "middle" }),
        text(0, 80.5, W, 5, "Computer Generated Invoice", { align: "center" }),
      ]),
    ],
    {
      defaults: FONT,
      variables: [{ name: "net_amount", expr: cfg.netVar }],
    }
  );
}

// PURCHASE RETURN INVOICE (ReturnInvoice_1, called from pdfhelper.ts ReturnInvoice with
// (resultArray, store, taxDetails, null, isDiscount): taxDetails is an array, so the GST
// summary is printed, the title stays "PURCHASE RETURN INVOICE" and isDiscount is unused).
// Totals fall back to item sums when the order column is blank/0, like calculateTotals().
export function purchaseReturnA4() {
  return purchaseDocA4({
    docType: "purchase_return",
    name: "Purchase Return (A4) - current format",
    title: "PURCHASE RETURN INVOICE",
    noLabel: "Purchase Return No",
    noExpr: "COALESCE(order.invoice_number, order.customer_invoice_number, order.prt_invoice_number)",
    dateLabel: "Purchase Return Date",
    dateExpr: "COALESCE(order.update_date, order.document_date)",
    // The original prints the pay-to address under "Ship To" and the ship-to address under "Ship From".
    left: ["Ship From", "order.prt_ship_to_address", "ship"],
    right: ["Ship To", "order.prt_pay_to_address", "pay"],
    totals: [
      ["GrossTotal:", "IF(order.prt_sub_total, order.prt_sub_total, SUM(items.item_price_wiithout_tax))"],
      ["TaxAmount:", "IF(order.prt_total_gst, order.prt_total_gst, SUM(items.item_tax_amount))"],
      ["Discount:", "IF(order.prt_total_discount, order.prt_total_discount, SUM(items.item_discount_amount))"],
      ["Other Charge:", "0"],
      ["Round Off:", "COALESCE(order.prt_round_off_value, 0)"],
      ["TotalAmount:", "var.net_amount"],
    ],
    netVar: "IF(order.prt_total_amount, order.prt_total_amount, SUM(items.item_total_amount))",
    netExpr: "var.net_amount",
    remarksExpr: "COALESCE(order.prt_remarks, order.icn_remarks, order.remarks, \"\")",
  });
}

// PURCHASE CREDIT NOTE (DebitNote_1, called with (resultArray, store, taxdetails, isDiscount)).
export function purchaseCreditNoteA4() {
  return purchaseDocA4({
    docType: "purchase_credit_note",
    name: "Purchase Credit / Debit Note (A4) - current format",
    title: "PURCHASE CREDIT NOTE",
    noLabel: "Credit Note No",
    noExpr: "order.pct_custom_invoice_number",
    dateLabel: "Credit Note Date",
    dateExpr: "COALESCE(order.update_date, order.pct_document_date)",
    left: ["Bill To", "order.pct_pay_to_address", "pay"],
    right: ["Ship To", "order.pct_ship_to_address", "ship"],
    totals: [
      ["GrossTotal:", "COALESCE(order.pct_sub_total, 0)"],
      ["TaxAmount:", "COALESCE(order.pct_total_gst, 0)"],
      ["Discount:", "COALESCE(order.pct_invoice_discount, 0)"],
      ["Other Charge:", "0"],
      ["Round Off:", "COALESCE(order.pct_round_off_value, 0)"],
      ["TotalAmount:", "var.net_amount"],
    ],
    netVar: "COALESCE(order.pct_total_amount, 0)",
    netExpr: "var.net_amount",
    remarksExpr: "COALESCE(order.remarks, \"\")",
  });
}
